import type { ReactNode } from 'react'
import { cx } from './cx'

/**
 * A table that scrolls inside its own box rather than widening the page.
 *
 * Audit rows and sync history are naturally wide, and a page that scrolls
 * horizontally as a whole takes the navigation off-screen with it.
 */
export function TableWrap({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cx('w-full overflow-x-auto', className)}>
      <table className="w-full min-w-[44rem] border-collapse text-sm">{children}</table>
    </div>
  )
}

export function Th({
  children,
  className,
  scope = 'col',
}: {
  children?: ReactNode
  className?: string
  scope?: 'col' | 'row'
}) {
  return (
    <th
      scope={scope}
      className={cx(
        'border-b border-ink-200 bg-ink-50 px-4 py-2.5 text-left text-xs font-semibold tracking-wide text-ink-600 uppercase',
        className
      )}
    >
      {children}
    </th>
  )
}

export function Td({
  children,
  className,
  colSpan,
}: {
  children?: ReactNode
  className?: string
  /** For a detail row that spans the table — an expanded report draft, say. */
  colSpan?: number
}) {
  return (
    <td
      colSpan={colSpan}
      className={cx('border-b border-ink-100 px-4 py-3 align-middle text-ink-800', className)}
    >
      {children}
    </td>
  )
}

export function EmptyRow({ colSpan, children }: { colSpan: number; children: ReactNode }) {
  return (
    <tr>
      <td colSpan={colSpan} className="px-4 py-10 text-center text-sm text-ink-600">
        {children}
      </td>
    </tr>
  )
}
